import {
    faArrowRight,
    faDotCircle,
    faDown,
    faDownLeft,
    faDownRight,
    faDrawPolygon,
    faLeft,
    faRight,
    faTrafficLight,
    faUp,
    faUpLeft,
    faUpRight,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/react";
import * as turf from "@turf/turf";
import { bearing, point } from "@turf/turf";
import { notification } from "antd";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { Feature, FeatureCollection } from "geojson";
import mapboxgl from "mapbox-gl";
import {
    MapboxStyleDefinition,
    MapboxStyleSwitcherControl,
    MapboxStyleSwitcherOptions,
} from "mapbox-gl-style-switcher";
import React from "react";
import { Layer } from "react-map-gl/mapbox";
import { globalAppConfig } from "../../config";
import { GlobalStoreContextType } from "../../context/GlobalStoreContext";
import Helper from "../../Helper";
import mapIcons from "../../mapIcons";
import { Area } from "../../models/Area";
import { CalendarEvent } from "../../models/CalendarEvent";
import { Delivery } from "../../models/Delivery";
import { Dropoff } from "../../models/Dropoff";
import { Equipment } from "../../models/Equipment";
import { Gate } from "../../models/Gate";
import { GateToDropoffRoute } from "../../models/GateToDropoffRoute";
import { HaulRoute } from "../../models/HaulRoute";
import { MapObject } from "../../models/MapObject";
import { MapObjectRadius } from "../../models/MapObjectRadius";
import { RepeatingCalendarEventDefinition } from "../../models/RepeatingCalendarEventDefinition";
import { DropoffGate } from "../../models/DropoffGate";
import { WaypointDirection } from "../../models/WaypointDirection";
import { WaypointStep } from "../../models/WaypointStep";
import PlotAPI from "../../plotAPI";
import TeamInfo from "../../teamInfo";

// dayjs.extend(isSameOrBefore);
// dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
// dayjs.extend(utc);

// A gate can serve a dropoff unless that pair has a banned DropoffGate row. With no dropoff chosen yet, every gate
// is allowed.
export function gateAllowedAtDropoff(dropoffGates: DropoffGate[], gateId: number, dropoffId: number | null) {
    if (dropoffId == null) {
        return true;
    }

    return !dropoffGates.some(
        (dropoffGate) => dropoffGate.gateId === gateId && dropoffGate.dropoffId === dropoffId && dropoffGate.banned
    );
}

// The gate a dropoff's deliveries default to. A dropoff with only one allowed gate always defaults to it, even if nobody
// marked it as the default - there's no other gate a delivery there could use.
export function defaultGateIdForDropoff(
    dropoffGates: DropoffGate[],
    gates: Gate[],
    dropoffId: number
): number | undefined {
    const markedDefault = dropoffGates.find(
        (dropoffGate) => dropoffGate.dropoffId === dropoffId && dropoffGate.isDefault
    );
    if (markedDefault) {
        return markedDefault.gateId;
    }

    const allowedGates = gates.filter(
        (gate) => !gate.archived && gateAllowedAtDropoff(dropoffGates, gate.id, dropoffId)
    );
    return allowedGates.length === 1 ? allowedGates[0].id : undefined;
}

// A delivery's Gate-to-Dropoff Routes follow from its gate and dropoff - there's nothing to pick, and the server looks
// them up the same way (CalendarEvent#delivery_gate_to_dropoff_route).
export function gateToDropoffRoutesForPair(
    routes: GateToDropoffRoute[],
    gateId: number | null,
    dropoffId: number | null
): GateToDropoffRoute[] {
    if (gateId == null || dropoffId == null) {
        return [];
    }

    return routes.filter((route) => !route.archived && route.gateId === gateId && route.dropoffId === dropoffId);
}

export function dropoffOpen(dropoffMapObject: MapObject, date: Dayjs | null, dropoffs: Dropoff[]) {
    if (!date) {
        return true;
    }

    if (!dropoffMapObject || !dropoffMapObject.mappableId) {
        return false;
    }

    const item = dropoffs.find((item) => item.id === dropoffMapObject.mappableId);

    if (!item) {
        return true;
    }

    const dropoffOpenHoursObject = item.repeatingCalendarEventDefinitions?.find(
        (def: any) => def.template_calendar_event.calendar_event_type === "availability"
    );

    if (dropoffOpenHoursObject) {
        // Guessing timezone here as we dont actually care about the render just the compare
        const openHours = RepeatingCalendarEventDefinition.fromApiObject(dropoffOpenHoursObject, dayjs.tz.guess());

        if (openHours.scheduleStart && date.isBefore(openHours.scheduleStart, "day")) {
            return false;
        }

        if (
            openHours.scheduleEnd &&
            (date.isAfter(openHours.scheduleEnd, "day") || date.isSame(openHours.scheduleEnd, "day"))
        ) {
            return false;
        }
    }

    return true;
}

export function equipmentOpen(equipment: Equipment, date: Dayjs | null) {
    if (!date) {
        return true;
    }

    const equipmentOpenHoursObject = equipment.repeatingCalendarEventDefinitions?.find(
        (def: any) => def.template_calendar_event.calendar_event_type === "availability"
    );

    if (equipmentOpenHoursObject) {
        // Guessing timezone here as we dont actually care about the render just the compare
        const openHours = RepeatingCalendarEventDefinition.fromApiObject(equipmentOpenHoursObject, dayjs.tz.guess());

        if (openHours.scheduleStart && date.isBefore(openHours.scheduleStart, "day")) {
            return false;
        }
        if (
            openHours.scheduleEnd &&
            (date.isAfter(openHours.scheduleEnd, "day") || date.isSame(openHours.scheduleEnd, "day"))
        ) {
            return false;
        }
    }

    return true;
}

export function addressFormater(projectInfo) {
    let addressString = "";
    const addressValues = [
        projectInfo.address_1 || projectInfo.address1,
        projectInfo.address_2 || projectInfo.address2,
        projectInfo.city,
        projectInfo.state,
        projectInfo.zip_code || projectInfo.zipCode,
    ];

    addressValues.forEach((val) => {
        if (val) {
            addressString += val;
            if (val === projectInfo.state || val === projectInfo.zip_code) {
                addressString += " ";
            } else {
                addressString += ", ";
            }
        }
    });

    return addressString;
}

export function iconType(objectType: string) {
    if (objectType === "point") {
        return faDotCircle;
    } else if (objectType === "polygon") {
        return faDrawPolygon;
    } else {
        return faArrowRight;
    }
}

export function geocoderHelperResultFormatter(result) {
    result.context.push({
        id: result.id,
        text: result.text,
    });
    const location = {
        name: null,
        address: null,
        city: null,
        zip: null,
        state: null,
        country: null,
        lon: result.geometry.coordinates[0],
        lat: result.geometry.coordinates[1],
    };
    if (result.place_type.includes("address")) {
        location.address = result.place_name.substring(0, result.place_name.indexOf(","));
    }
    if (result.place_type.includes("poi")) {
        location.address = result.properties.address;
        location.name = result.place_name.substring(0, result.place_name.indexOf(","));
    }
    location.city = result.context.find((x) => {
        return x.id.startsWith("place");
    }).text;
    if (
        result.context.find((x) => {
            return x.id.startsWith("postcode");
        })
    ) {
        location.zip = result.context.find((x) => {
            return x.id.startsWith("postcode");
        }).text;
    }
    location.state = result.context.find((x) => {
        return x.id.startsWith("region");
    }).text;
    location.country = result.context.find((x) => {
        return x.id.startsWith("country");
    }).text;
    return location;
}

export function validateZipCode(val) {
    return /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(val);
}

export function findLayerIndex(array, val) {
    return array.findIndex((layer) => layer.features.find((feature) => feature.id === val));
}

export function isMobileDevice() {
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        return true;
    } else {
        return false;
    }
}

const waypointLineStringConstructor = (mapObject: MapObject) => {
    let wayPoints = "";

    // Only use the last 10 coordinates for waypoints
    const coordsToUse = mapObject.coordinates.slice(-10);
    coordsToUse.forEach((coord) => {
        wayPoints += `${coord[1]},${coord[0]}|`;
    });

    return wayPoints;
};

const mapObjectLatLng = (mapObject: MapObject): string => {
    if (mapObject.geometry === "lineString") {
        return `${mapObject.coordinates[0][1]}, ${mapObject.coordinates[0][0]}`;
    }

    const center = mapObject.getCenter();
    return `${center[1]},${center[0]}`;
};

export function openMapLink(endPoint: MapObject, waypoints: MapObject[] = []) {
    if (!endPoint) {
        return;
    }

    const destination = mapObjectLatLng(endPoint);

    const url = new URL("https://www.google.com/maps/dir/?api=1&travelmode=driving&origin=My+Location");
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS && destination) {
        url.protocol = "maps:";
        url.host = "maps.google.com";
    }

    url.searchParams.append("destination", destination);

    if (waypoints.length > 0) {
        url.searchParams.append("waypoints", waypoints.map(mapObjectLatLng).join("|"));
    }

    window.open(url.toString());
}

export const getMapObjectCenter = (object: MapObject) => {
    const points = turf.points(object.coordinates);

    return turf.center(points);
};

export const getMapMaxMetersPerPixelAtZoom = (map: mapboxgl.Map) => {
    if (!map) {
        return 1; // Default to 1 meter per pixel if map is not available
    }
    const y = map.getCanvas()?.height;
    const x = map.getCanvas()?.width;

    const left = map.unproject([0, y]);
    const bottom = map.unproject([x, y]);
    const maxMeters =
        turf.distance(turf.point([left.lng, left.lat]), turf.point([bottom.lng, bottom.lat]), {
            units: "miles",
        }) * 1609.344;
    const metersPerPixel = maxMeters / x;
    return metersPerPixel;
};

export const getIconRealWorldSize = (mapObject: MapObject, metersPerPixel: number) => {
    let size = mapObject.iconWidth || 1;
    if (mapObject.iconUnits !== "meters") {
        size = size * 0.3048;
    }
    const iconPixels = size / metersPerPixel;
    const iconSize = iconPixels / 128; // 128 is the default size of the icon in pixels. All icons are 128x128
    if (iconSize && iconSize !== undefined && iconSize !== Infinity) {
        return iconSize;
    } else {
        return 1;
    }
};

export const defaultIconSizeArray = (iconType: boolean): any[] => {
    const svgIconSizeArray = ["interpolate", ["linear"], ["zoom"], 12, 0, 13, 0.1, 14, 0.5, 16, 1, 17, 0.5];
    const iconSizeArray = ["interpolate", ["linear"], ["zoom"], 12, 0, 13, 0.1, 14, 0.2, 16, 0.25];

    if (iconType) {
        return iconSizeArray;
    }
    return svgIconSizeArray;
};

export const mapStyles: MapboxStyleDefinition[] = [
    {
        title: "Satellite-ESRI",
        uri: globalAppConfig.ARC_GIS_IMAGERY_URL,
    },
    // Breaks when using react-map-gl
    // {
    //     title: "Satellite-Mapbox",
    //     uri: `mapbox://styles/mapbox/standard-satellite`,
    // },
    {
        title: "Streets",
        uri: "mapbox://styles/mapbox/streets-v12",
    },
    {
        title: "Satellite Streets",
        uri: "mapbox://styles/mapbox/satellite-streets-v12",
    },
    {
        title: "Dark",
        uri: "mapbox://styles/mapbox/dark-v11",
    },
    {
        title: "Light",
        uri: "mapbox://styles/mapbox/light-v11",
    },
    {
        title: "Outdoors",
        uri: "mapbox://styles/mapbox/outdoors-v12",
    },
];

export const addMapControls = (map: mapboxgl.Map, mapStyleOptions: MapboxStyleSwitcherOptions) => {
    if (!map) {
        return;
    }
    //top-left controls

    // top-right controls
    map.addControl(new mapboxgl.GeolocateControl(), "top-right");
    map.addControl(new mapboxgl.NavigationControl(), "top-right");
    map.addControl(new mapboxgl.FullscreenControl(), "top-right");
    map.addControl(new MapboxStyleSwitcherControl(mapStyles, mapStyleOptions), "top-right");

    // bottom-right controls
    map.addControl(new mapboxgl.ScaleControl(), "bottom-right");
};

// TODO: This is being added to try and help debug `addToBounds` below.
const extractLngLatPairs = (input: any): [number, number][] => {
    const result: [number, number][] = [];
    const invalidItems: any[] = [];

    const recurse = (item: any) => {
        if (Array.isArray(item) && item.length === 2 && typeof item[0] === "number" && typeof item[1] === "number") {
            result.push(item as [number, number]);
        } else if (Array.isArray(item)) {
            item.forEach(recurse);
        } else {
            invalidItems.push(item);
        }
    };

    recurse(input);

    if (invalidItems.length > 0) {
        Sentry.captureMessage("Non-lng/lat items encountered in extractLngLatPairs", {
            level: "warning",
            extra: {
                invalidItems,
                originalInput: input,
            },
        });
    }

    return result;
};

export const addToBounds = (object: MapObject, currentBounds: mapboxgl.LngLatBounds): mapboxgl.LngLatBounds => {
    try {
        const bounds = currentBounds || new mapboxgl.LngLatBounds();

        const lngLatPairs = extractLngLatPairs(object.coordinates);
        lngLatPairs.forEach((pair) => bounds.extend(pair));

        return bounds;
    } catch (error) {
        Sentry.captureException(error, {
            extra: {
                object,
                currentBounds,
            },
        });
        return new mapboxgl.LngLatBounds();
    }
};

export const generateMapGeoJson = (mapObjects: MapObject[]): FeatureCollection => {
    const geoJsonData: { type: string; features: any[] } = {
        type: "FeatureCollection",
        features: [],
    };

    mapObjects
        .filter((mapObject: MapObject) => mapObject instanceof MapObject)
        .forEach((mapObject: MapObject) => {
            geoJsonData.features.push(mapObject.toFeature());
            if (mapObject.geometry == "lineString") {
                geoJsonData.features = geoJsonData.features.concat(arrowCapSourceBuilder(mapObject));
            }
            if (mapObject.radiuses && mapObject.radiuses.length > 0) {
                mapObject.radiuses.forEach((radius) => {
                    geoJsonData.features.push(radius.toFeature(mapObject));
                });
            }
        });

    return geoJsonData as FeatureCollection;
};

export const addGeoJsonSourceToMap = (
    map: mapboxgl.Map,
    sourceId: string,
    mapObjects: MapObject[],
    context: GlobalStoreContextType
) => {
    if (map.getSource(sourceId)) {
        return;
    }
    const data = generateMapGeoJson(mapObjects);

    return map.addSource(sourceId, {
        type: "geojson",
        data: data,
        // Cannot use dynamic here because it causes issues with rendering polygons
        // dynamic: true,
    });
};

export const addIcon = (
    object: MapObject,
    sourceId: string,
    map: mapboxgl.Map,
    visibility: "visible" | "none",
    iconSize: number | any[]
) => {
    return (
        <>
            {object.radiuses && object.radiuses.length > 0 && (
                <React.Fragment>
                    {object.radiuses.map((radius) => {
                        return (
                            <React.Fragment key={`${object.uuid}_radius_${radius.id}`}>
                                <Layer
                                    key={`${object.uuid}_radius_${radius.id}`}
                                    id={`${object.uuid}_radius_${radius.id}`}
                                    slot="top"
                                    source={sourceId}
                                    type="fill"
                                    paint={{
                                        "fill-opacity": radius.opacity || 1,
                                        "fill-color": radius.color || "#ff9e00",
                                    }}
                                    layout={{
                                        visibility: visibility,
                                    }}
                                    filter={["==", ["get", "id"], `${object.uuid}_radius_${radius.id}`]}
                                />

                                <Layer
                                    key={`${object.uuid}_radius_border_${radius.id}`}
                                    id={`${object.uuid}_radius_border_${radius.id}`}
                                    slot="top"
                                    source={sourceId}
                                    type="line"
                                    layout={{
                                        visibility: radius.showBorder ? visibility : "none",
                                    }}
                                    paint={{
                                        "line-color": radius.color || "#ff9e00",
                                        "line-width": 2,
                                        "line-opacity": 1,
                                    }}
                                    filter={["==", ["get", "id"], `${object.uuid}_radius_${radius.id}`]}
                                />

                                {radius.name &&
                                    map &&
                                    map.isStyleLoaded() &&
                                    !map?.getStyle()?.sprite?.includes("arcgis") && (
                                        <Layer
                                            key={`${object.uuid}_radius_label_${radius.id}`}
                                            id={`${object.uuid}_radius_label_${radius.id}`}
                                            slot="top"
                                            source={sourceId}
                                            type="symbol"
                                            layout={{
                                                visibility: visibility,
                                                "text-field": radius.name,
                                                "symbol-placement": "line",

                                                "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
                                                "text-size": 12,
                                                "text-anchor": "center",
                                                "text-allow-overlap": true,
                                            }}
                                            paint={{
                                                "text-color": "#FFFFFF",
                                                "text-halo-color": "#222222",
                                                "text-halo-width": 1,
                                            }}
                                            filter={["==", ["get", "id"], `${object.uuid}_radius_${radius.id}`]}
                                        />
                                    )}
                            </React.Fragment>
                        );
                    })}
                </React.Fragment>
            )}
            <Layer
                id={object.uuid}
                slot="top"
                source={sourceId}
                type="symbol"
                layout={{
                    visibility: visibility,
                    "icon-image": object.icon || "bullet_sdf_icon",
                    "icon-size": iconSize,
                    "icon-anchor": object.iconAnchor ? object.mapboxIconAnchor() : "center",
                    "icon-allow-overlap": true,
                    "icon-ignore-placement": true,
                }}
                paint={{
                    "icon-color": object.color || "#ff9e00",
                    "icon-opacity": object.opacity || 1,
                    "icon-halo-color": "#ffffff",
                    "icon-halo-width": 0,
                    "icon-translate-transition": { duration: 2000 },
                    "icon-halo-width-transition": { duration: 200 },
                }}
                filter={["==", ["get", "id"], object.uuid]}
            />
        </>
    );
};

// arrow_sdf_icon (like all map icons) is a 128x128 asset (see getIconRealWorldSize) - sized up well
// past the primary line width (5) since the glyph has enough transparent padding around it that a
// smaller size rendered as a barely-visible sliver.
// The color a new gate-to-dropoff route starts with, rather than taking on its gate's or dropoff's color. It can be
// changed per route with the Line Color picker like any other line. Direction comes from its waypoint step icons
// instead of arrows along the line.
export const GATE_TO_DROPOFF_ROUTE_COLOR = "#3b82f6";

export const addLine = (
    object: MapObject,
    sourceId: string,
    visibility: "visible" | "none",
    iconSize: number | any[]
) => {
    const isGateToDropoffRoute = object.mappableType === "GateToDropoffRoute";

    return (
        <React.Fragment key={object.uuid}>
            <Layer
                id={object.uuid}
                slot="top"
                source={sourceId}
                type="line"
                paint={{
                    "line-color":
                        object.outline ||
                        object.color ||
                        (isGateToDropoffRoute ? GATE_TO_DROPOFF_ROUTE_COLOR : "#ff9e00"),
                    "line-width": isGateToDropoffRoute ? 6 : 5,
                    "line-opacity": object.opacity ? object.opacity : 1.0,
                }}
                layout={{
                    visibility: visibility,
                    "line-join": "round",
                    "line-cap": "round",
                }}
                filter={["==", ["get", "id"], object.uuid]}
            />
            {/* Always added, even with no icon yet, so the icon picker has layers to update live (see locationIcon.tsx);
                an empty icon-image draws nothing. Before, a line saved without an icon - every Gate-to-Dropoff Route
                starts that way - had no layers, so picking an icon showed nothing until after Save. */}
            <React.Fragment>
                <Layer
                    key={`${object.uuid}_line_icon`}
                    id={`${object.uuid}_line_icon`}
                    slot="top"
                    source={sourceId}
                    type="symbol"
                    layout={{
                        visibility: visibility,
                        "symbol-placement": "line",
                        "symbol-spacing": 1,
                        "icon-image": object.icon ?? "",
                        "icon-size": iconSize,
                        "icon-allow-overlap": true,
                        "icon-rotate": object.icon && object.icon.includes("no-entry") ? 90 : 0,
                    }}
                    paint={{
                        "icon-color": object.color || object.outline || "#ff9e00",
                    }}
                    filter={["==", ["get", "id"], object.uuid]}
                />
                <Layer
                    key={`${object.uuid}_lineCaps`}
                    id={`${object.uuid}_lineCaps`}
                    slot="top"
                    source={sourceId}
                    type="symbol"
                    layout={{
                        visibility: visibility,
                        "icon-image": object.icon ?? "",
                        "icon-size": iconSize,
                        "icon-rotate": ["get", "rotation"],
                        "icon-anchor": "center",
                        "icon-rotation-alignment": "map",
                        "icon-allow-overlap": true,
                        "icon-ignore-placement": true,
                    }}
                    paint={{
                        "icon-color": object.color || object.outline || "#ff9e00",
                    }}
                    filter={["in", ["get", "id"], ["literal", [`${object.uuid}_startCap`, `${object.uuid}_endCap`]]]}
                />
            </React.Fragment>
        </React.Fragment>
    );
};

export const addPolygon = (
    object: MapObject,
    sourceId: string,
    visibility: "visible" | "none",
    iconSize: number | any[]
) => {
    return (
        <React.Fragment key={object.uuid}>
            <Layer
                key={object.uuid}
                id={object.uuid}
                slot="top"
                source={sourceId}
                type="fill"
                paint={{
                    "fill-color": object.color || "#ff9e00",
                    "fill-opacity": object.opacity || 0.75,
                    "fill-opacity-transition": { duration: 200 },
                }}
                layout={{
                    visibility: visibility,
                }}
                filter={["==", ["get", "id"], object.uuid]}
            />

            <Layer
                key={`${object.uuid}_outline`}
                id={`${object.uuid}_outline`}
                slot="top"
                source={sourceId}
                type="line"
                paint={{
                    "line-color": object.outline || object.color || "#ff9e00",
                    "line-width": 2,
                    "line-width-transition": { duration: 200 },
                    "line-dasharray": [10],
                }}
                layout={{
                    visibility: visibility,
                }}
                filter={["==", ["get", "id"], object.uuid]}
            />
            {object.icon && (
                <Layer
                    key={`${object.uuid}_outline_icon`}
                    id={`${object.uuid}_outline_icon`}
                    slot="top"
                    source={sourceId}
                    type="symbol"
                    layout={{
                        visibility: visibility,
                        "symbol-placement": "line",
                        "symbol-spacing": 1,
                        "icon-image": object.icon ? object.icon : "arrow_sdf_icon",
                        "icon-size": iconSize,
                        // ["interpolate", ["linear"], ["zoom"], 10, 0.7, 15, 0.3],
                        "icon-allow-overlap": true,
                        "icon-rotate": object.icon && object.icon.includes("no-entry") ? 90 : 0,
                    }}
                    paint={{
                        "icon-color": object.outline || object.color || "#ff9e00",
                        "icon-halo-color": "#ffffff",
                        "icon-halo-width": 0,
                        "icon-halo-width-transition": { duration: 200 },
                    }}
                    filter={["==", ["get", "id"], object.uuid]}
                />
            )}
        </React.Fragment>
    );
};

export const addPolygonIconBorder = async (
    map: mapboxgl.Map,
    object: MapObject,
    layerId: string,
    visibility: "visible" | "none",
    iconSize = 1
) => {
    return map.addLayer({
        id: `${object.uuid}_outline_icon`,
        slot: "top",
        type: "symbol",
        source: layerId,
        layout: {
            visibility: visibility,
            "symbol-placement": "line",
            "symbol-spacing": 1,
            "icon-image": object.icon ? object.icon : "arrow_sdf_icon",
            "icon-size": iconSize,
            // ["interpolate", ["linear"], ["zoom"], 10, 0.7, 15, 0.3],
            "icon-allow-overlap": true,
            "icon-rotate": object.icon && object.icon.includes("no-entry") ? 90 : 0,
        },
        paint: {
            "icon-color": object.outline || object.color || "#ff9e00",
        },

        filter: ["==", ["get", "id"], object.uuid],
    });
};

const arrowCapSourceBuilder = (object: MapObject): Feature[] => {
    return [
        {
            id: `${object.uuid}_startCap`,
            type: "Feature",
            properties: {
                id: `${object.uuid}_startCap`,
                rotation:
                    object.icon && object.icon.includes("no-entry")
                        ? getStartCapBearing(object) + 90 + 90
                        : getStartCapBearing(object) + 90,
            },
            geometry: {
                type: "Point",
                coordinates: object.coordinates[0],
            },
        },
        {
            id: `${object.uuid}_endCap`,
            type: "Feature",
            geometry: {
                type: "Point",
                coordinates: object.coordinates.at(-1),
            },
            properties: {
                id: `${object.uuid}_startCap`,
                rotation:
                    object.icon && object.icon.includes("no-entry")
                        ? getEndCapBearing(object) + 270 + 90
                        : getEndCapBearing(object) + 270,
            },
        },
    ];
};

const getEndCapBearing = (object: MapObject) => {
    if (object.coordinates?.length < 2) {
        return 0; // Not enough points to calculate bearing
    }
    const point1 = point(object.coordinates[object.coordinates.length - 2]);
    const point2 = point(object.coordinates[object.coordinates.length - 1]);
    const mapBearing = bearing(point1, point2);
    return mapBearing;
};
const getStartCapBearing = (object: MapObject) => {
    if (object.coordinates?.length < 2) {
        return 0; // Not enough points to calculate bearing
    }
    const point1 = point(object.coordinates[1]);
    const point2 = point(object.coordinates[0]);
    const mapBearing = bearing(point1, point2);
    return mapBearing;
};
export const addMarkerImages = async (map: mapboxgl.Map) => {
    const flatMap = [
        ...mapIcons.equipmentIcons,
        ...mapIcons.sdfIcons,
        ...mapIcons.letterIcons,
        ...mapIcons.numberIcons,
    ];

    return Promise.all(
        flatMap
            .filter((icon) => icon !== undefined && icon !== "")
            .map((icon) => {
                return new Promise<void>((resolve, reject) => {
                    if (map.hasImage(icon)) {
                        return resolve();
                    }
                    map.loadImage(`/img/png/map/${icon}.png`, (_error, image) => {
                        if (!image) {
                            return resolve();
                        }
                        // Add the image to the map style.
                        map.addImage(icon, image, { sdf: icon.includes("sdf") ? true : false });
                        return resolve();
                    });
                });
            })
    );
};

export const generateInteractiveLayerIds = (mapObjects: MapObject[]) => {
    const interactiveLayerIds: string[] = [];

    mapObjects.forEach((mapObject) => {
        interactiveLayerIds.push(mapObject.uuid);
        if (mapObject.geometry === "lineString" && mapObject.icon) {
            interactiveLayerIds.push(`${mapObject.uuid}_line_icon`);
            interactiveLayerIds.push(`${mapObject.uuid}_lineCaps`);
        }
        if (mapObject.geometry === "polygon") {
            interactiveLayerIds.push(`${mapObject.uuid}_outline`);
            if (mapObject.icon) {
                interactiveLayerIds.push(`${mapObject.uuid}_outline_icon`);
            }
        }
        if (mapObject.radiuses && mapObject.radiuses.length > 0) {
            mapObject.radiuses.forEach((radius) => {
                interactiveLayerIds.push(`${mapObject.uuid}_radius_${radius.id}`);
                if (radius.showBorder) {
                    interactiveLayerIds.push(`${mapObject.uuid}_radius_border_${radius.id}`);
                }
                if (radius.name) {
                    interactiveLayerIds.push(`${mapObject.uuid}_radius_label_${radius.id}`);
                }
            });
        }
    });

    return interactiveLayerIds;
};

export const addNecessaryMarkerImages = async (icons: string[], map: mapboxgl.Map) => {
    return Promise.all(
        icons
            .filter((icon) => icon !== undefined && icon !== "")
            .map((icon) => {
                return new Promise<void>((resolve, reject) => {
                    map.loadImage(`/img/png/map/${icon}.png`, (error, image) => {
                        if (error) {
                            reject(error);
                            return;
                        }
                        if (!image) {
                            resolve();
                            return;
                        }
                        // Add the image to the map style.
                        if (!map.hasImage(icon)) {
                            map.addImage(icon, image, { sdf: icon.includes("sdf") ? true : false });
                        }
                        resolve();
                    });
                });
            })
    );
};

export const toggleShowOpenLocations = (
    map: mapboxgl.Map,
    context: GlobalStoreContextType,
    mapObjects: MapObject[],
    date: Dayjs = dayjs()
) => {
    mapObjects.forEach((mapObject) => {
        const mappable = mapObject.getMappable(context);
        if (!mappable) {
            return;
        }

        let visibility: "visible" | "none" = "none";

        if (mappable instanceof Area) {
            // Map markup has no hours, so it's always "open".
            visibility = "visible";
        } else if (
            mappable instanceof Dropoff &&
            dropoffOpen(mapObject, date, context.dropoffsForProject(context.currentProjectInfo.unique_token))
        ) {
            visibility = "visible";
        } else if (mappable instanceof Equipment && equipmentOpen(mappable, date)) {
            visibility = "visible";
        } else if (mappable instanceof Gate && mappable.openOnDay(date)) {
            visibility = "visible";
        } else if (mappable instanceof HaulRoute) {
            // Haul routes have no hours of their own.
            visibility = "visible";
        }

        mapObject.setVisibility(map, visibility);
    });
};

export const deliveryMapToggleDisplayLogic = (
    mapRef: mapboxgl.Map,
    context: GlobalStoreContextType,
    date: Dayjs,
    mapObjects: MapObject[] = []
) => {
    if (!mapRef) {
        return;
    }

    const eventsForDay = context.calendarEventsByProject
        .flatMap((project) => project.calendarEvents)
        .filter((event) => event.calendarableType === "Delivery" && event.startAt.isSame(date, "day"));

    mapObjects.forEach((mapObject) => {
        const mappable = mapObject.getMappable(context);
        if (!mappable) {
            return;
        }
        let visibility: "visible" | "none" = "none";
        if (calendarShowArea(context, mappable, eventsForDay)) {
            visibility = "visible";
        }
        mapObject.setVisibility(mapRef, visibility);
    });
};

export const calendarShowArea = (
    context: GlobalStoreContextType,
    mappable: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute,
    eventsForDay: CalendarEvent[]
) => {
    if (
        mappable instanceof Dropoff &&
        context.calendarEventsFilterInfo.calendarEventResources.find(
            (resource) =>
                resource.calendarResourceableType === "Dropoff" && resource.calendarResourceableId === mappable.id
        )
    ) {
        return true;
    }

    if (
        mappable instanceof Equipment &&
        context.calendarEventsFilterInfo.calendarEventResources.find(
            (resource) =>
                resource.calendarResourceableType === "Equipment" && resource.calendarResourceableId === mappable.id
        )
    ) {
        return true;
    }

    if (mappable instanceof Dropoff && eventsForDay.some((event) => event.deliveryDropOffId() === mappable.id)) {
        return true;
    }

    if (
        mappable instanceof Equipment &&
        eventsForDay.some((event) => event.deliveryEquipmentIds().some((id) => id === mappable.id))
    ) {
        return true;
    }

    const filteredToThis = (type: "Gate" | "HaulRoute") =>
        context.calendarEventsFilterInfo.calendarEventResources.some(
            (resource) => resource.calendarResourceableType === type && resource.calendarResourceableId === mappable.id
        );

    if (
        mappable instanceof Gate &&
        (filteredToThis("Gate") || eventsForDay.some((event) => event.deliveryGateId() === mappable.id))
    ) {
        return true;
    }

    if (
        mappable instanceof HaulRoute &&
        (filteredToThis("HaulRoute") || eventsForDay.some((event) => event.deliveryHaulRouteId() === mappable.id))
    ) {
        return true;
    }

    return false;
};
type mapWeatherEndpoints =
    | "temperature"
    | "dewPoint"
    | "humidity"
    | "windSpeed"
    | "windDirection"
    | "precipitationIntensity"
    | "visibility"
    | "lightning";

export const openEditAreaMapHandler = (
    mappable: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute,
    context: GlobalStoreContextType
) => {
    const mapObject = mappable.getMapObject(context);
    if (!mapObject) {
        return;
    }
    context.setEditCurrentMappable(true);

    if (!context.mapRef?.current) {
        return;
    }

    const map = context.mapRef.current.getMap();

    if (!map || !context.mapBoxDraw) return;

    // Cancelling "add map item" removes the draw control from the map (see addMapItemMenu's cancelDrawMode), and every
    // draw call throws while it's detached, so put it back before using it.
    if (!map.hasControl(context.mapBoxDraw)) {
        map.addControl(context.mapBoxDraw, "top-left");
    }

    // Clear out whatever feature was left over from editing the previous mappable - otherwise it
    // stays loaded in the draw control alongside the new one and can interfere with editing it.
    context.mapBoxDraw.deleteAll();
    context.mapBoxDraw.add(mapObject.toFeature());
    if (mapObject?.geometry === "point") {
        // direct_select throws for Point features ("direct_select mode doesn't handle point
        // features") - the equivalent for a point is simple_select with it pre-selected, which
        // makes it draggable immediately instead of requiring a click to select it first.
        context.mapBoxDraw.changeMode("simple_select", { featureIds: [mapObject.uuid] });
    } else {
        context.mapBoxDraw.changeMode("direct_select", { featureId: mapObject?.uuid });
    }
    map.flyTo({
        center: mapObject?.getCenter(),
        essential: true, // this animation is considered essential with respect to prefers-reduced-motion
    });
    // Mapbox draw doesn't put the draw layers on top of other layers by default, so we need to move them to the top
    const mapDrawLayers = map.getStyle().layers?.filter((layer) => layer.id.startsWith("gl-draw"));

    mapDrawLayers?.forEach((layer) => {
        map.moveLayer(layer.id);
    });
};

export const archiveMapObjectRemoveLayers = (
    context: GlobalStoreContextType,
    map: mapboxgl.Map,
    mapObject: MapObject
) => {
    mapObject.removeLayerFromMapbox(map);
    context.setNewMappableType(null);
};

export const addGeotiff = (map: mapboxgl.Map, geotiff: any) => {
    if (!map || !geotiff) {
        return;
    }

    if (map.getSource(`${geotiff.id}_geotiff`) || map.getLayer(`${geotiff.id}_geotiff`)) {
        return;
    }

    const geotiffUrl = `mapbox://nickdreiling.${geotiff.geotiff_name}`;
    map.addSource(`${geotiff.id}_geotiff`, {
        type: "raster",
        url: geotiffUrl,
    }).addLayer({
        id: `${geotiff.id}_geotiff`,
        type: "raster",
        source: `${geotiff.id}_geotiff`,
    });
};

export const setMapView = (map: mapboxgl.Map | null | undefined, projectInfo: any) => {
    if (!map) {
        return;
    }

    const center = map.getCenter();
    const zoom = map.getZoom();
    const style = map.getStyle().sprite?.includes("arcgis")
        ? globalAppConfig.ARC_GIS_IMAGERY_URL
        : map.getStyle().sprite;
    const newProjectInfo = projectInfo;
    newProjectInfo.lat_override = center.lat;
    newProjectInfo.lon_override = center.lng;
    newProjectInfo.map_zoom = zoom;
    newProjectInfo.map_style = style;

    PlotAPI.putProjectInfo(newProjectInfo.unique_token, newProjectInfo).then((response) => {
        if (response) {
            notification.success({
                message: "Default Map View Updated",
                description: "The map view has been saved to your jobsite successfully.",
            });
            return { success: true, message: "Default Map View Updated" };
        } else {
            notification.error({
                message: "Failed to Update Map View",
                description: "There was an issue saving the map view. Please try again.",
            });
            return { success: false, message: "Failed to Update Map View" };
        }
    });
};
export const updateMapIconSize = (map: mapboxgl.Map, iconSize: number, mapObject: MapObject) => {
    if (!map || !mapObject) {
        return;
    }
    let layerId = mapObject.uuid;

    if (mapObject.geometry == "polygon") {
        layerId = `${mapObject.uuid}_outline_icon`;
    }
    if (mapObject.geometry == "lineString") {
        layerId = `${mapObject.uuid}_line_icon}`;
    }

    if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, "icon-size", iconSize);
    }

    if (map.getLayer(`${mapObject.uuid}_lineCaps`)) {
        map.setLayoutProperty(`${mapObject.uuid}_lineCaps`, "icon-size", iconSize);
    }
};

const updateWaypointDirection = (
    context: GlobalStoreContextType,
    mappable: Area | HaulRoute | GateToDropoffRoute,
    mapObject: MapObject,
    newCoordinates: [number, number][] | [number, number]
) => {
    const waypointDirection = mappable.waypointDirection;

    if (!waypointDirection || !waypointDirection.waypointSteps || waypointDirection.waypointSteps.length === 0) {
        return;
    }

    // If this waypoint is a point it is not an array of coordinates so we need to handle that separately
    if (mapObject.geometry === "point") {
        updateWaypointPoint(context, mappable, newCoordinates as [number, number]);
        return;
    }

    if (waypointDirection.waypointSteps.length === newCoordinates.length) {
        updateWaypointStepsCoordinates(context, mappable, newCoordinates);
        return;
    }

    if (waypointDirection.waypointSteps.length < newCoordinates.length) {
        addWaypointStep(context, mappable, mapObject, newCoordinates);
        return;
    }

    if (waypointDirection.waypointSteps.length > newCoordinates.length) {
        deleteWaypointStep(context, mappable, newCoordinates);
        return;
    }
};

const addWaypointStep = (
    context: GlobalStoreContextType,
    mappable: Area | HaulRoute | GateToDropoffRoute,
    mapObject: MapObject,
    newCoordinates: [number, number][]
) => {
    const waypointDirection = mappable.waypointDirection;
    if (!waypointDirection?.waypointSteps) {
        return;
    }

    const newStepIndex = newCoordinates.findIndex(
        (nc) => !waypointDirection.waypointSteps.find((ws) => ws.coordinates[0] == nc[0] && ws.coordinates[1] == nc[1])
    );
    // Copy icon/color from the preceding step so the new one matches its neighbor's styling - but
    // a step inserted at the very start of the line (newStepIndex === 0) has no preceding step, so
    // fall back to the step that was first (soon to be second) instead of indexing at -1.
    const referenceStep = waypointDirection.waypointSteps[Math.max(newStepIndex - 1, 0)];
    // Only the start and finish get an icon by default; copying the neighbor's here would put a
    // second start flag on a step inserted right after the first one.
    const isIntermediateStep = newStepIndex > 0 && newStepIndex < newCoordinates.length - 1;
    const newStep = WaypointStep.fromApiObject(
        {
            step_number: newStepIndex + 1,
            coordinates: newCoordinates[newStepIndex],
            icon: isIntermediateStep ? null : referenceStep.icon,
            instruction: `Proceed to waypoint step ${newStepIndex + 1}`,
            color: referenceStep.color,
            icon_rotate: 0,
        },
        context.currentProjectInfo.timezone
    );

    waypointDirection.waypointSteps.splice(newStepIndex, 0, newStep);

    // Update step numbers for subsequent steps
    waypointDirection.waypointSteps.forEach((step, idx) => {
        if (idx >= newStepIndex) {
            step.stepNumber = idx + 1;
        }
    });
    const tempMappable = Object.create(mappable) as Area | HaulRoute | GateToDropoffRoute;
    tempMappable.waypointDirection = waypointDirection;
    context.setCurrentMappable(tempMappable);
};

const updateWaypointPoint = (
    context: GlobalStoreContextType,
    mappable: Area | HaulRoute | GateToDropoffRoute,
    newCoordinate: [number, number]
) => {
    const tempMappable = Object.create(mappable) as Area | HaulRoute | GateToDropoffRoute;
    if (!tempMappable.waypointDirection) {
        return;
    }
    const tempWaypointDirection = Object.create(tempMappable.waypointDirection) as WaypointDirection;
    if (!tempWaypointDirection?.waypointSteps || tempWaypointDirection.waypointSteps.length === 0) {
        return;
    }

    tempWaypointDirection.waypointSteps = tempWaypointDirection.waypointSteps.map((step) => {
        const tempStep = Object.create(step) as WaypointStep;
        tempStep.coordinates = newCoordinate;
        return tempStep;
    });

    tempMappable.waypointDirection = tempWaypointDirection;

    context.setCurrentMappable(tempMappable);
};

const updateWaypointStepsCoordinates = (
    context: GlobalStoreContextType,
    mappable: Area | HaulRoute | GateToDropoffRoute,
    newCoordinates: [number, number][]
) => {
    const tempMappable = Object.create(mappable) as Area | HaulRoute | GateToDropoffRoute;
    if (!tempMappable.waypointDirection) {
        return;
    }
    const tempWaypointDirection = Object.create(tempMappable.waypointDirection) as WaypointDirection;
    if (
        !tempMappable.waypointDirection ||
        !tempWaypointDirection?.waypointSteps ||
        tempWaypointDirection.waypointSteps.length === 0
    ) {
        return;
    }

    tempWaypointDirection.waypointSteps = tempWaypointDirection.waypointSteps.map((step, index) => {
        const tempStep = Object.create(step) as WaypointStep;
        tempStep.coordinates = newCoordinates[index];
        return tempStep;
    });

    tempMappable.waypointDirection = tempWaypointDirection;

    context.setCurrentMappable(tempMappable);
};

const deleteWaypointStep = (
    context: GlobalStoreContextType,
    mappable: Area | HaulRoute | GateToDropoffRoute,
    newCoordinates: [number, number][]
) => {
    if (!mappable.waypointDirection) {
        return;
    }
    const waypointDirection = Object.create(mappable.waypointDirection) as WaypointDirection;
    if (!waypointDirection?.waypointSteps) {
        return;
    }

    const stepIndexToDelete = waypointDirection.waypointSteps.findIndex(
        (ws) => !newCoordinates.find((nc) => ws.coordinates[0] == nc[0] && ws.coordinates[1] == nc[1])
    );

    if (stepIndexToDelete === -1) {
        return;
    }

    // waypointDirection.waypointSteps.splice(stepIndexToDelete, 1);
    waypointDirection.waypointSteps[stepIndexToDelete]._destroy = true;

    // Update step numbers for subsequent steps
    waypointDirection.waypointSteps
        .filter((step) => !step._destroy)
        .forEach((step, idx) => {
            if (idx >= stepIndexToDelete) {
                step.stepNumber = idx + 1;
            }
        });
    const tempMappable = Object.create(mappable) as Area | HaulRoute | GateToDropoffRoute;
    tempMappable.waypointDirection = waypointDirection;
    context.setCurrentMappable(tempMappable);
};

// Keeps a gate-to-dropoff route's endpoint glued to its gate/dropoff while that gate/dropoff is
// being dragged on the map, so the route doesn't visually detach from what it connects to. Mutates
// the matching route entries directly in `projectMapObjects` - the same clone about to be fed into
// generateMapGeoJson - so the update lands in the same render as the drag itself, rather than as a
// separate imperative paint call (unlike the color preview, this changes the feature's geometry,
// which needs to flow through the normal GeoJSON source data rather than a paint property).
const updateAttachedGateToDropoffRouteEndpoints = (
    context: GlobalStoreContextType,
    projectMapObjects: MapObject[],
    mappable: Gate | Dropoff,
    draggedMapObject: MapObject
) => {
    const isGate = mappable instanceof Gate;
    // A polygon Dropoff has no single "position" while dragged - use its centroid, same as
    // Helper.onDrawCreate/syncDropoffToRoutes do for the persisted version of this.
    const newEndpoint = draggedMapObject.getCenter() as [number, number];

    context
        .gateToDropoffRoutesForProject(context.currentProjectInfo.unique_token)
        .filter((route) => !route.archived && (isGate ? route.gateId === mappable.id : route.dropoffId === mappable.id))
        .forEach((route) => {
            const routeMapObject = projectMapObjects.find(
                (object) => object.mappableType === "GateToDropoffRoute" && object.mappableId === route.id
            );
            if (!routeMapObject?.coordinates?.length) {
                return;
            }
            // Reassign (rather than mutate in place) since routeMapObject is an Object.create
            // clone whose `coordinates` is otherwise inherited from - and shared with - the
            // original cached MapObject; mutating the array in place would corrupt that cache.
            const newCoordinates = [...routeMapObject.coordinates];
            const index = isGate ? 0 : newCoordinates.length - 1;
            newCoordinates[index] = newEndpoint;
            routeMapObject.coordinates = newCoordinates;
        });
};

export const onDrawUpdate = (e: mapboxgl.MapMouseEvent & { features: any[] }, context: GlobalStoreContextType) => {
    const newCurrentMapObject = Object.create(context.currentMapObject) as MapObject;

    if (
        (context.currentMappable instanceof Area ||
            context.currentMappable instanceof HaulRoute ||
            context.currentMappable instanceof GateToDropoffRoute) &&
        context.currentMappable.waypointDirection
    ) {
        updateWaypointDirection(
            context,
            context.currentMappable,
            newCurrentMapObject,
            e.features[0].geometry.coordinates
        );
    }

    newCurrentMapObject.coordinates = e.features[0].geometry.coordinates;
    context.setCurrentMapObject(newCurrentMapObject);
    const projectMapObjects = context.mapObjectsForProject(context.currentProjectInfo.unique_token).map((x) => {
        return Object.create(x) as MapObject;
    });
    if (projectMapObjects.find((x) => x.uuid === newCurrentMapObject.uuid)) {
        projectMapObjects.find((x) => x.uuid === newCurrentMapObject.uuid)!.coordinates =
            newCurrentMapObject.coordinates;
    } else {
        projectMapObjects.push(newCurrentMapObject);
    }

    if (context.currentMappable instanceof Gate || context.currentMappable instanceof Dropoff) {
        updateAttachedGateToDropoffRouteEndpoints(
            context,
            projectMapObjects,
            context.currentMappable,
            newCurrentMapObject
        );
    }

    context.setMapGeoJson(generateMapGeoJson(projectMapObjects));
};

export const onDrawCreate = (e: mapboxgl.MapMouseEvent & { features: any[] }, context: GlobalStoreContextType) => {
    const newMapObject = MapObject.fromFeature(e.features[0], context.currentProjectInfo, context.newMappableType);

    if (context.newMappableType === "gate_to_dropoff_route" && context.pendingGateToDropoffRoute) {
        const projectMapObjects = context.mapObjectsForProject(context.currentProjectInfo.unique_token);
        const gateMapObject = projectMapObjects.find(
            (mapObject) =>
                mapObject.mappableType === "Gate" && mapObject.mappableId === context.pendingGateToDropoffRoute!.gateId
        );
        const dropoffMapObject = projectMapObjects.find(
            (mapObject) =>
                mapObject.mappableType === "Dropoff" &&
                mapObject.mappableId === context.pendingGateToDropoffRoute!.dropoffId
        );

        newMapObject.color = GATE_TO_DROPOFF_ROUTE_COLOR;
        newMapObject.outline = null;

        // Routes are drawn by clicking points in whatever order the operator chooses, but the waypoint steps generated
        // below follow the line's own coordinate order - so normalize it here to always run gate -> dropoff, regardless
        // of which end was clicked first.
        if (gateMapObject && dropoffMapObject && newMapObject.coordinates?.length > 1) {
            const routeStart = newMapObject.coordinates[0];
            const distanceToGate = turf.distance(routeStart, gateMapObject.getCenter() as [number, number]);
            const distanceToDropoff = turf.distance(routeStart, dropoffMapObject.getCenter() as [number, number]);

            if (distanceToDropoff < distanceToGate) {
                newMapObject.coordinates = [...newMapObject.coordinates].reverse();
            }
        }
    }

    context.setCurrentMapObject(newMapObject);

    const newMappable =
        context.newMappableType == "equipment"
            ? Equipment.fromFeature(e.features[0], context.currentProjectInfo, context.currentProjectInfo.timezone)
            : context.newMappableType == "gate"
              ? Gate.fromFeature(e.features[0], context.currentProjectInfo, context.currentProjectInfo.timezone)
              : context.newMappableType == "haul_route"
                ? HaulRoute.fromFeature(e.features[0], context.currentProjectInfo, context.currentProjectInfo.timezone)
                : context.newMappableType == "gate_to_dropoff_route" && context.pendingGateToDropoffRoute
                  ? GateToDropoffRoute.fromFeature(
                        e.features[0],
                        context.currentProjectInfo,
                        context.pendingGateToDropoffRoute.gateId,
                        context.pendingGateToDropoffRoute.dropoffId,
                        context.pendingGateToDropoffRoute.name,
                        context.currentProjectInfo.timezone
                    )
                  : context.newMappableType == "dropoff"
                    ? Dropoff.fromFeature(
                          e.features[0],
                          context.currentProjectInfo,
                          context.currentProjectInfo.timezone
                      )
                    : Area.fromFeature(e.features[0], context.currentProjectInfo, context.currentProjectInfo.timezone);

    // Every HaulRoute/GateToDropoffRoute is itself the driving path, so seed its turn-by-turn steps
    // immediately on creation rather than requiring a separate opt-in toggle the way Areas used to.
    if (newMappable instanceof HaulRoute || newMappable instanceof GateToDropoffRoute) {
        newMappable.waypointDirection = WaypointDirection.fromApiObject(
            {
                waypointable_type: newMappable instanceof HaulRoute ? "HaulRoute" : "GateToDropoffRoute",
                waypointable_id: newMappable.id || null,
                waypoint_steps: [],
            },
            context.currentProjectInfo.timezone
        );
        newMappable.waypointDirection.waypointSteps = generateWaypointSteps(
            newMappable,
            newMapObject,
            context.currentProjectInfo.timezone
        );
    }

    context.setPendingGateToDropoffRoute(null);
    context.setCurrentMappable(newMappable);

    context.setEditCurrentMappable(true);
    context.setEditCurrentMappablePosition(true);

    const projectMapObjects = context.mapObjectsForProject(context.currentProjectInfo.unique_token);
    if (!projectMapObjects.find((x) => x.uuid === newMapObject.uuid)) {
        projectMapObjects.push(newMapObject);
    }
    context.setMapGeoJson(generateMapGeoJson(projectMapObjects));
    return newMapObject;
};

export const updateRadiusSize = (
    mapObject: MapObject,
    radius: MapObjectRadius,
    distance: number,
    units: "feet" | "meters" = "feet",
    index: number,
    context: GlobalStoreContextType
) => {
    if (!mapObject) {
        return;
    }
    const newCurrentMapObject = Object.create(mapObject) as MapObject;
    const newRadiuses = (newCurrentMapObject.radiuses || []).map((o) => Object.create(o) as MapObjectRadius);
    newRadiuses[index].radius = distance;
    newRadiuses[index].units = units;
    if (!newCurrentMapObject.radiuses) {
        newCurrentMapObject.radiuses = [newRadiuses[index]];
    } else {
        newCurrentMapObject.radiuses[index] = newRadiuses[index];
    }
    const radiusId = radius.id || index;
    const radiusFeatureIndex = context.mapGeoJson?.features.findIndex(
        (f) => f.id === `${newCurrentMapObject.uuid}_radius_${radiusId}`
    );
    context.setCurrentMapObject(newCurrentMapObject);

    const center = newCurrentMapObject.coordinates;
    const options = { units: units, steps: 10, properties: { id: `${newCurrentMapObject.uuid}_radius_${radiusId}` } };
    const diameter = turf.circle(center, distance, options) as GeoJSON.Feature;

    const tempGeoJson = { ...context.mapGeoJson } as GeoJSON.FeatureCollection;

    if (radiusFeatureIndex === -1) {
        // Feature not found; optionally log or handle this case
        return;
    }
    tempGeoJson.features[radiusFeatureIndex].geometry = diameter.geometry;
    context.setMapGeoJson(tempGeoJson);
};

export const randomColorGenerator = () => {
    return "#" + ((Math.random() * 0xffffff) << 0).toString(16).padStart(6, "0");
};

export const resetMapObject = (context: GlobalStoreContextType, mapObject: MapObject) => {
    const mappable = mapObject.getMappable(context);
    if (!mappable) {
        return;
    }
    const originalMapObject = mappable.getMapObject(context);
    if (!originalMapObject) {
        return;
    }
    const map = context.mapRef?.current?.getMap();
    if (!map) {
        return;
    }
    map.setPaintProperty(`${originalMapObject.uuid}`, "fill-color", originalMapObject.color || "#ff9e00");
};

// Opening an item in the legend panel isolates the map down to that item and whatever it's directly
// related to. Returns null when nothing is open, i.e. there's no isolation to apply.
const isolatedMapObjectVisibility = (
    context: GlobalStoreContextType,
    mappable: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute,
    project: any
): "visible" | "none" | null => {
    const currentMappable = context.currentMappable;
    const dropoffGates = context.dropoffGatesForProject(project.unique_token);

    // Only a hover from the open item's own relationship list may stand in for its default - not hoveredMapObject,
    // which the legend beside that list also sets for every item it shows. The pair also has to belong to the open item
    // and not be banned, so a stale hover from a previously open item can't leak through either.
    const previewPair = context.hoveredDropoffGatePair;
    const previewAllowed =
        !!previewPair &&
        !dropoffGates.some(
            (dg) => dg.gateId === previewPair.gateId && dg.dropoffId === previewPair.dropoffId && dg.banned
        );

    // Opening a Gate shows that gate and every dropoff it's allowed to serve, with each one's route from it. Hovering a
    // row in its "Allowed Dropoffs" list focuses the map on just that dropoff and its route.
    if (currentMappable instanceof Gate) {
        const previewDropoffId =
            previewAllowed && previewPair.gateId === currentMappable.id ? previewPair.dropoffId : null;

        const dropoffShown = (dropoffId: number) => {
            if (previewDropoffId !== null) {
                return dropoffId === previewDropoffId;
            }
            return gateAllowedAtDropoff(dropoffGates, currentMappable.id, dropoffId);
        };

        if (mappable instanceof Gate) {
            return mappable.id === currentMappable.id ? "visible" : "none";
        }
        if (mappable instanceof GateToDropoffRoute) {
            return mappable.gateId === currentMappable.id && !mappable.archived && dropoffShown(mappable.dropoffId)
                ? "visible"
                : "none";
        }
        if (mappable instanceof Dropoff) {
            return !mappable.archived && dropoffShown(mappable.id) ? "visible" : "none";
        }
        // A Haul Route, markup Area or Equipment is unrelated to whichever Gate is currently open.
        return "none";
    }

    // Inverse of the Gate case above: opening a Dropoff shows every gate it allows, with each one's route to it, and
    // hovering a row in its "Allowed Gates" list focuses the map on just that gate and its route.
    if (currentMappable instanceof Dropoff) {
        const previewGateId =
            previewAllowed && previewPair.dropoffId === currentMappable.id ? previewPair.gateId : null;
        const gateShown = (gateId: number) =>
            previewGateId !== null
                ? gateId === previewGateId
                : gateAllowedAtDropoff(dropoffGates, gateId, currentMappable.id);

        if (mappable instanceof Dropoff) {
            return mappable.id === currentMappable.id ? "visible" : "none";
        }
        if (mappable instanceof GateToDropoffRoute) {
            return mappable.dropoffId === currentMappable.id && !mappable.archived && gateShown(mappable.gateId)
                ? "visible"
                : "none";
        }
        if (mappable instanceof Gate) {
            return !mappable.archived && gateShown(mappable.id) ? "visible" : "none";
        }
        return "none";
    }

    // Opening an Equipment isolates the map down to just that piece of equipment.
    if (currentMappable instanceof Equipment) {
        return mappable instanceof Equipment && mappable.id === currentMappable.id ? "visible" : "none";
    }

    // Opening a Haul Route isolates the map down to just that route.
    if (currentMappable instanceof HaulRoute) {
        return mappable instanceof HaulRoute && mappable.id === currentMappable.id ? "visible" : "none";
    }

    // Opening (or editing) a Gate-to-Dropoff Route shows that route and the gate and dropoff at its two ends. Without
    // this it fell through to the "routes stay hidden" rule below, so editing a route showed only Mapbox Draw's line.
    if (currentMappable instanceof GateToDropoffRoute) {
        if (mappable instanceof GateToDropoffRoute) {
            return mappable.id === currentMappable.id ? "visible" : "none";
        }
        if (mappable instanceof Gate) {
            return mappable.id === currentMappable.gateId ? "visible" : "none";
        }
        if (mappable instanceof Dropoff) {
            return mappable.id === currentMappable.dropoffId ? "visible" : "none";
        }
        return "none";
    }

    return null;
};

export const mapObjectVisibility = (context: GlobalStoreContextType, mapObject: MapObject, project: any) => {
    if (mapObject.mappableId === -1) {
        // Newly created map objects that have not been saved yet
        return "visible";
    }
    const mappable = mapObject.getMappable(context);

    if (!mappable) {
        return "none";
    }

    // Hovering a Gate-to-Dropoff Route (from either the gate's page or the dropoff's page) isolates
    // the map down to just that route, its gate, and its dropoff - hiding everything else, including
    // other gates, other dropoffs, equipment, and haul routes, so the pairing is unambiguous.
    const hoveredRoute =
        context.hoveredMapObject?.mappableType === "GateToDropoffRoute"
            ? (context.hoveredMapObject.getMappable(context) as GateToDropoffRoute | null)
            : null;

    if (hoveredRoute) {
        if (mappable instanceof GateToDropoffRoute) {
            return mappable.id === hoveredRoute.id ? "visible" : "none";
        }
        if (mappable instanceof Gate) {
            return mappable.id === hoveredRoute.gateId ? "visible" : "none";
        }
        if (mappable instanceof Dropoff) {
            return mappable.id === hoveredRoute.dropoffId ? "visible" : "none";
        }
        // Anything else (e.g. a HaulRoute) is unrelated to the hovered Gate-to-Dropoff Route.
        return "none";
    }

    const isolatedVisibility = isolatedMapObjectVisibility(context, mappable, project);

    if (isolatedVisibility === "visible") {
        return "visible";
    }

    // Editors can opt out of the isolation to see what else is around the item they're placing;
    // anything the isolation would have hidden then falls back to the normal map filters below.
    if (isolatedVisibility === "none" && !(context.editCurrentMappable && context.showOtherMapItemsWhileEditing)) {
        return "none";
    }

    if (mappable instanceof GateToDropoffRoute) {
        // Gate-to-Dropoff Routes clutter the map badly if left on all the time, so outside of the
        // isolate behaviors above, they're hidden unless a gate or dropoff is currently open.
        return "none";
    }
    if (context.activeSidebarKey == "deliveries") {
        const eventsForDay = context.calendarEventsByProject
            .flatMap((project) => project.calendarEvents)
            .filter(
                (event) => event.calendarableType === "Delivery" && event.startAt.isSame(context.mapFilterDate, "day")
            );

        return calendarShowArea(context, mappable, eventsForDay) ? "visible" : "none";
    } else if (context.mapFilterDate) {
        if (mappable instanceof Dropoff) {
            const dropoffOpenStatus = dropoffOpen(
                mapObject,
                context.mapFilterDate,
                context.dropoffsForProject(project.unique_token)
            );
            return dropoffOpenStatus ? "visible" : "none";
        } else if (mappable instanceof Equipment) {
            const equipmentOpenStatus = equipmentOpen(mappable, context.mapFilterDate);
            return equipmentOpenStatus ? "visible" : "none";
        }
    }
    return "visible";
};

export const loadMapLayers = (mapObjects: MapObject[], context: GlobalStoreContextType, layerId: string) => {
    return mapObjects.map((object, idx) => {
        if (!object || !context.mapRef?.current) {
            return null;
        }

        const mappable = object.getMappable(context);

        if (!mappable) {
            return null;
        }

        const visibility = Helper.mapObjectVisibility(context, object);

        switch (object.geometry) {
            case "point":
                return Helper.addIcon(object, layerId, context.mapRef?.current.getMap(), visibility);
            case "lineString":
                return Helper.addLine(object, layerId, visibility);
            case "polygon":
                return Helper.addPolygon(object, layerId, visibility);
            default:
                return null;
        }
    });
};

export const mappableName = (item: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute): string => {
    if (item instanceof Area) {
        return item.areaName || "";
    }
    if (item instanceof Equipment) {
        return item.equipmentName || "";
    }
    return item.name || "";
};

export const mappableType = (item: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute): string => {
    if (item instanceof Area) {
        return "Area";
    }
    if (item instanceof Dropoff) {
        return "Dropoff";
    }
    if (item instanceof Equipment) {
        return "Equipment";
    }
    if (item instanceof Gate) {
        return "Gate";
    }
    if (item instanceof HaulRoute) {
        return "Haul Route";
    }
    if (item instanceof GateToDropoffRoute) {
        return "Gate to Dropoff Route";
    }
    return "Unknown";
};

// The model name the backend uses for this item (e.g. a map object's mappable_type). instanceof rather than
// constructor.name, which doesn't survive minification.
export const mappableApiType = (
    item: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute
): "Area" | "Dropoff" | "Equipment" | "Gate" | "HaulRoute" | "GateToDropoffRoute" => {
    if (item instanceof Area) {
        return "Area";
    }
    if (item instanceof Dropoff) {
        return "Dropoff";
    }
    if (item instanceof Equipment) {
        return "Equipment";
    }
    if (item instanceof Gate) {
        return "Gate";
    }
    if (item instanceof HaulRoute) {
        return "HaulRoute";
    }
    return "GateToDropoffRoute";
};

export const mappableObjectReplicator = (item: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute) => {
    if (item instanceof Area) {
        return Object.create(item) as Area;
    }
    if (item instanceof Dropoff) {
        return Object.create(item) as Dropoff;
    }
    if (item instanceof Equipment) {
        return Object.create(item) as Equipment;
    }
    if (item instanceof Gate) {
        return Object.create(item) as Gate;
    }
    if (item instanceof HaulRoute) {
        return Object.create(item) as HaulRoute;
    }
    if (item instanceof GateToDropoffRoute) {
        return Object.create(item) as GateToDropoffRoute;
    }
    return null;
};

export const sortMapAreasByType = (
    a: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute,
    b: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute
) => {
    // Dropoffs sort ahead of everything else.
    const getStrength = (item: Area | Dropoff | Equipment | Gate | HaulRoute) => (item instanceof Dropoff ? 2 : 0);

    const aStrength = getStrength(a);
    const bStrength = getStrength(b);

    // Sort by strength first
    if (aStrength !== bStrength) {
        return bStrength - aStrength;
    }

    // Then sort by name
    return mappableName(a).localeCompare(mappableName(b));
};
export interface LogisticsDeliveriesByDay {
    [day: string]: {
        deliveryDate: dayjs.Dayjs;
        deliveryTeams: {
            [teamName: string]: {
                team: TeamInfo;
                deliveries: Delivery[];
            };
        };
    };
}

export const layerImagerySource = (url: string) => {
    switch (true) {
        case /dronedeploy.com/i.test(url):
            return "DroneDeploy";
        case /propelleraero.com/i.test(url):
            return "Propeller";
        case /mapbox.com/i.test(url):
            return "Mapbox";
        case /arcgis.com/i.test(url):
            return "ArcGIS";
        case /esri.com/i.test(url):
            return "Esri";
        default:
            return "Other";
    }
};

const bearingToDirectionAndIcon = (b: number) => {
    let direction = "North";
    let icon = faUp;
    if (b > 22.5 && b <= 67.5) {
        direction = "Northeast";
        icon = faUpRight;
    } else if (b > 67.5 && b <= 112.5) {
        direction = "East";
        icon = faRight;
    } else if (b > 112.5 && b <= 157.5) {
        direction = "Southeast";
        icon = faDownRight;
    } else if (b > 157.5 && b <= 202.5) {
        direction = "South";
        icon = faDown;
    } else if (b > 202.5 && b <= 247.5) {
        direction = "Southwest";
        icon = faDownLeft;
    } else if (b > 247.5 && b <= 292.5) {
        direction = "West";
        icon = faLeft;
    } else if (b > 292.5 && b <= 337.5) {
        direction = "Northwest";
        icon = faUpLeft;
    }

    return { direction, icon };
};

export const waypointDirectionsStepsCalc = (waypoint: Area, mapObject: MapObject) => {
    const waypointSteps: any[] = [];
    if (!waypoint || !mapObject) {
        return waypointSteps;
    }

    waypointSteps.push({
        children: (
            <div>
                Enter <span className="font-semibold">{waypoint.areaName}</span> waypoint
            </div>
        ),
        dot: <FontAwesomeIcon icon={faTrafficLight} />,
    });

    // Check if the coordinates are a single array with two numbers (a single point), return early as we do not have steps to reach there
    if (mapObject.geometry == "point") {
        return waypointSteps;
    }

    for (let i = 0; i < mapObject.coordinates.length - 1; i++) {
        const point1 = point(mapObject.coordinates[i]);
        const point2 = point(mapObject.coordinates[i + 1]);
        let unit = "miles";
        let d = turf.distance(point1, point2, { units: "miles" });
        if (d < 0.1) {
            d = d * 5280;
            unit = "feet";
        }
        const b = turf.bearingToAzimuth(turf.bearing(point1, point2));
        const { direction, icon } = bearingToDirectionAndIcon(b);

        waypointSteps.push({
            children: `Head ${direction} for ${d.toFixed(2)} ${unit}`,
            dot: <FontAwesomeIcon icon={icon} />,
        });
    }

    return waypointSteps;
};

export const waypointDirectionApiBuilder = (oldWaypoint: WaypointDirection, newWaypoint: WaypointDirection) => {
    const tempWaypoint = newWaypoint;
    oldWaypoint.waypointSteps?.forEach((step) => {
        if (!newWaypoint.waypointSteps?.find((s) => s.id == step.id)) {
            const stepToDelete = Object.create(step) as WaypointStep;
            stepToDelete._destroy = true;
            tempWaypoint.waypointSteps?.push(stepToDelete);
        }
    });
    return tempWaypoint;
};

export const currentWaypointStepLogic = (currentWaypointStep: WaypointStep | null, step: WaypointStep | null) => {
    if (!currentWaypointStep || !step) {
        return false;
    }
    if (currentWaypointStep.stepNumber === step.stepNumber) {
        return true;
    }
    return false;
};

export const generateWaypointSteps = (
    mappable: Area | HaulRoute | GateToDropoffRoute,
    mapObject: MapObject,
    timezone: string
) => {
    const name = mappableName(mappable);

    if (mapObject.geometry == "point") {
        return [
            WaypointStep.fromApiObject(
                {
                    step_number: 1,
                    coordinates: mapObject.coordinates,
                    icon: null,
                    instruction: `Arrive at ${name} waypoint`,
                    color: "#F0F0F0",
                    icon_rotate: 0,
                },
                timezone
            ),
        ];
    }
    return mapObject.coordinates.map((coord: number[], index: number) => {
        let icon: string | null = null;
        let instruction = `Proceed to waypoint step ${index + 1}`;
        if (index === 0) {
            icon = "faFlagPennant";
            instruction = `Enter ${name} waypoint`;
        }
        if (index == mapObject.coordinates.length - 1) {
            icon = "faFlagCheckered";
            instruction = `Arrive at ${name} waypoint`;
        }

        return WaypointStep.fromApiObject(
            {
                step_number: index + 1,
                coordinates: coord,
                icon: icon,
                instruction: instruction,
                color: "#ff9e00",
                icon_rotate: 0,
            },
            timezone
        );
    });
};

export const mapObjectVisibilityRank = (mapObject: MapObject) => {
    // Should display polygons on bottom, then lines, then points above everything else.
    switch (mapObject.geometry) {
        case "polygon":
            return 0;
        case "lineString":
            return 1;
        case "point":
            return 2;
        default:
            return 3;
    }
};
